import type { ArticleTemplate } from "@vedaunion/shared";
import type { BlockDoc } from "../editor/types";
import { emptyDoc, newBlockId } from "../editor/utils";

/**
 * Pre-arranged article docs. Each template is a fully-built `BlockDoc` the
 * author can drop into the canvas as a starting point — heading + opening
 * paragraph + the right structure for that piece — and then edit. Authors
 * pick a template at create time; the block list is editable from then on.
 */

export interface TemplatePreset {
  key: ArticleTemplate;
  label: string;
  blurb: string;
  /** Pre-arranged doc — the starting article. */
  doc: BlockDoc;
  /** A short HTML preview snippet used by the picker as a thumbnail. */
  previewHtml: string;
}

const block = (type: string, data: unknown) => ({ id: newBlockId(), type, data });

export const TEMPLATES: TemplatePreset[] = [
  // -----------------------------------------------------------------------
  {
    key: "editorial",
    label: "Editorial",
    blurb: "Eyebrow → headline → drop-cap opening → pull-quote → close. The Veda Union house style.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "An editorial title",
          align: "left",
          eyebrow: "Reflection",
        }),
        block("paragraph", {
          html: "<p>Open with a single, considered sentence. The drop-cap on the very first letter sets the tempo for the whole piece — let it earn its place.</p>",
          align: "left",
          variant: "drop-cap",
        }),
        block("paragraph", {
          html: "<p>The second paragraph develops the thought. Keep paragraphs short. Trust the reader.</p>",
          align: "left",
          variant: "default",
        }),
        block("divider", { variant: "violet" }),
        block("heading", { level: 2, text: "A turning point", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Each section can carry its own quiet headline.</p>",
          align: "left",
          variant: "default",
        }),
        block("quote", {
          html: "<p>नमस्ते — A small Devanagari blessing or a Vedic line lives well as a pull-quote.</p>",
          citation: "",
          variant: "pull",
        }),
        block("paragraph", {
          html: "<p>Close on a single line that lingers. Make it true.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <h1>An Editorial</h1>
      <p><em>Drop-cap opens the piece with quiet authority.</em></p>
      <hr />
      <h2>A turning point</h2>
      <blockquote>A pull-quote.</blockquote>
    `,
  },

  // -----------------------------------------------------------------------
  {
    key: "verse-study",
    label: "Verse study",
    blurb: "Devanagari → IAST → English → source, then word-by-word + commentary. Canonical for scripture pieces.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "A verse worth dwelling on",
          align: "center",
          eyebrow: "Scripture",
        }),
        block("verse", {
          deva: "सत्यमेव जयते",
          iast: "Satyam eva jayate",
          english: "Truth alone triumphs.",
          source: "Muṇḍaka Upaniṣad 3.1.6",
        }),
        block("divider", { variant: "violet" }),
        block("heading", { level: 2, text: "Word by word", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Brief glosses for each word, in order. Useful for first-time readers.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "Commentary", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Several paragraphs of close reading. The historical context, the philosophical thrust, the lived implication.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <h1>A verse worth dwelling on</h1>
      <figure><p>सत्यमेव जयते</p><p><em>Satyam eva jayate</em></p></figure>
      <hr />
      <h2>Commentary</h2>
    `,
  },

  // -----------------------------------------------------------------------
  {
    key: "news",
    label: "News bulletin",
    blurb: "Headline → tight lede → next-step paragraph. For announcements and notices.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Headline of the bulletin",
          align: "left",
          eyebrow: "Announcement",
        }),
        block("paragraph", {
          html: "<p>A single tight paragraph delivers the news in plain language. When, where, who, why.</p>",
          align: "left",
          variant: "lead",
        }),
        block("paragraph", {
          html: "<p>A second paragraph adds the next-step detail readers need: a link, a date, a name to contact.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Announcement</p>
      <h1>Headline of the bulletin</h1>
      <p><em>A tight, single-paragraph lede.</em></p>
    `,
  },

  // -----------------------------------------------------------------------
  {
    key: "longform",
    label: "Long-form essay",
    blurb: "Hero cover → numbered sections → coda. For multi-part essays.",
    doc: {
      version: 1,
      blocks: [
        block("hero", {
          image: "",
          eyebrow: "Long read",
          title: "An essay in three movements",
          subtitle: "A subtitle that frames the question.",
          align: "center",
          variant: "overlay-dark",
          height: "medium",
        }),
        block("paragraph", {
          html: "<p>Open with a hook. A scene, a question, a contradiction. Earn the reader's attention by the first paragraph.</p>",
          align: "left",
          variant: "drop-cap",
        }),
        block("heading", { level: 2, text: "I. The first section", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Develop the idea. One claim per paragraph; one paragraph per beat.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "II. The second section", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Now turn the idea over. Contrast. Complicate.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "III. The third section", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Synthesise. The closing should feel both surprising and inevitable.</p>",
          align: "left",
          variant: "default",
        }),
        block("divider", { variant: "ornament" }),
        block("paragraph", {
          html: "<p><em>A coda — one line. A few words that the reader will carry.</em></p>",
          align: "center",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <section style="background:#2a1b47;color:#faf7f0;padding:1rem;text-align:center;"><p class="eyebrow" style="color:#a386cf">Long read</p><h1>Essay in three movements</h1></section>
      <h2>I. The first section</h2>
      <h2>II. The second section</h2>
      <h2>III. The third section</h2>
    `,
  },

  // -----------------------------------------------------------------------
  {
    key: "event-recap",
    label: "Event recap",
    blurb: "Hero cover → overview → photo gallery → reflections → next event.",
    doc: {
      version: 1,
      blocks: [
        block("hero", {
          image: "",
          eyebrow: "Recap",
          title: "A gathering at the House of Unity",
          subtitle: "Date · location",
          align: "center",
          variant: "overlay-dark",
          height: "short",
        }),
        block("paragraph", {
          html: "<p>A short overview paragraph: the date, the place, the form (satsang, fire-ceremony, retreat), and the headline take-away.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "What was offered", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>List the practices, the chants, the conversations.</p>",
          align: "left",
          variant: "default",
        }),
        block("gallery", { items: [], columns: 3, caption: "Photographs from the day." }),
        block("heading", { level: 2, text: "Reflections", align: "left", eyebrow: "" }),
        block("quote", {
          html: "<p>A short quotation from a participant — kept in their words.</p>",
          citation: "Participant",
          variant: "pull",
        }),
        block("cta", {
          eyebrow: "Coming up",
          title: "The next gathering",
          body: "A short blurb about what's next, with a link to the calendar entry.",
          buttonLabel: "View the calendar",
          buttonUrl: "/calendar",
          align: "center",
          variant: "vellum",
        }),
      ],
    },
    previewHtml: `
      <section style="background:#2a1b47;color:#faf7f0;padding:1rem;text-align:center;"><p class="eyebrow" style="color:#a386cf">Recap</p><h1>House of Unity</h1></section>
      <h2>What was offered</h2>
      <h2>Reflections</h2>
      <blockquote>A participant in their words.</blockquote>
    `,
  },

  // -----------------------------------------------------------------------
  // Minimal / functional templates — added per user request alongside the
  // richer presets above. Use these when you want less ornament.
  // -----------------------------------------------------------------------

  {
    key: "note",
    label: "Note",
    blurb: "A single-screen announcement: title → lead paragraph → callout. Minimal.",
    doc: {
      version: 1,
      blocks: [
        block("heading", { level: 1, text: "A short note", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>One paragraph delivers the news — when, where, what.</p>",
          align: "left",
          variant: "lead",
        }),
        block("callout", {
          variant: "note",
          title: "",
          html: "<p>Add a quick note or context here.</p>",
        }),
      ],
    },
    previewHtml: `
      <h1>A short note</h1>
      <p><em>One paragraph delivers the news.</em></p>
      <aside style="border-left:2px solid #a386cf;padding-left:.6rem;">A quick note.</aside>
    `,
  },

  {
    key: "image-essay",
    label: "Single image essay",
    blurb: "Hero image → drop-cap opening → two paragraphs. Photo-led short piece.",
    doc: {
      version: 1,
      blocks: [
        block("hero", {
          image: "",
          eyebrow: "",
          title: "An image-led piece",
          subtitle: "",
          align: "left",
          variant: "no-image",
          height: "short",
        }),
        block("paragraph", {
          html: "<p>Open against the image. Let it carry the mood.</p>",
          align: "left",
          variant: "drop-cap",
        }),
        block("paragraph", {
          html: "<p>Develop the thought in one or two more paragraphs. Keep it spare.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <section style="background:#eee;padding:1rem;text-align:center;"><h1>An image-led piece</h1></section>
      <p>Drop-cap opens.</p>
      <p>One more.</p>
    `,
  },

  {
    key: "reading-list",
    label: "Reading list",
    blurb: "Title → intro → numbered list of recommendations.",
    doc: {
      version: 1,
      blocks: [
        block("heading", { level: 1, text: "A reading list", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>A short note on why these — what they share, who they're for.</p>",
          align: "left",
          variant: "lead",
        }),
        block("list", {
          ordered: true,
          items: [
            "<p>First recommendation — a one-line gloss.</p>",
            "<p>Second recommendation — a one-line gloss.</p>",
            "<p>Third recommendation — a one-line gloss.</p>",
          ],
        }),
      ],
    },
    previewHtml: `
      <h1>A reading list</h1>
      <p><em>A short note on why these.</em></p>
      <ol><li>First.</li><li>Second.</li><li>Third.</li></ol>
    `,
  },

  {
    key: "class-transcript",
    label: "Class transcript",
    blurb: "Title → lead → alternating section heading + paragraph. For talks turned into articles.",
    doc: {
      version: 1,
      blocks: [
        block("heading", { level: 1, text: "Notes from a class", align: "left", eyebrow: "Class" }),
        block("paragraph", {
          html: "<p>A short framing paragraph — date, teacher, theme.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "Section one", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>The first beat of the talk, paraphrased.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "Section two", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>The second beat, with a verse or quotation if any was offered.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "Section three", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Closing reflections.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Class</p>
      <h1>Notes from a class</h1>
      <h2>Section one</h2>
      <h2>Section two</h2>
      <h2>Section three</h2>
    `,
  },

  // -----------------------------------------------------------------------
  // Generic platform templates — News article (a fuller cousin of the news
  // bulletin), Report, Announcement, Interview. Designed to match common
  // editorial briefs without imposing the heavy editorial / longform house
  // style.
  // -----------------------------------------------------------------------

  {
    key: "news-article",
    label: "News article",
    blurb: "Cover image → eyebrow + headline → byline → lead → 3 sections → next-step.",
    doc: {
      version: 1,
      blocks: [
        block("hero", {
          image: "",
          eyebrow: "News",
          title: "A clear, factual headline",
          subtitle: "An optional subtitle that adds essential context.",
          align: "left",
          variant: "no-image",
          height: "short",
        }),
        block("paragraph", {
          html: "<p>The lead paragraph answers who, what, when, where, why in a single beat. Keep it to 2–3 sentences.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "What happened", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>The first body section unpacks the lead — a short narrative or a list of facts.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "Why it matters", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>One or two paragraphs of context. Quote a source if you have one.</p>",
          align: "left",
          variant: "default",
        }),
        block("quote", {
          html: "<p>An optional pull-quote from someone involved.</p>",
          citation: "",
          variant: "side",
        }),
        block("heading", { level: 2, text: "What's next", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>A closing paragraph with the next step — date, link, or follow-up.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">News</p>
      <h1>A clear, factual headline</h1>
      <p><em>The lead answers who, what, when, where.</em></p>
      <h2>What happened</h2>
      <h2>Why it matters</h2>
      <h2>What's next</h2>
    `,
  },

  {
    key: "announcement",
    label: "Announcement",
    blurb: "Big eyebrow → punchy headline → short body → call-to-action card.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "An announcement",
          align: "center",
          eyebrow: "Announcement",
        }),
        block("paragraph", {
          html: "<p>One paragraph that delivers the news directly. State what's happening, who it's for, and the timing.</p>",
          align: "center",
          variant: "lead",
        }),
        block("paragraph", {
          html: "<p>A second paragraph adds the supporting detail readers will want.</p>",
          align: "left",
          variant: "default",
        }),
        block("cta", {
          eyebrow: "Take part",
          title: "What's the next step",
          body: "A short blurb explaining what readers do next, with a clear destination.",
          buttonLabel: "Continue",
          buttonUrl: "#",
          align: "center",
          variant: "vellum",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow" style="text-align:center;">Announcement</p>
      <h1 style="text-align:center;">An announcement</h1>
      <p style="text-align:center;"><em>One paragraph that delivers the news directly.</em></p>
      <aside style="border:1px solid #d4cdb8;padding:.75rem;text-align:center;"><strong>What's the next step</strong></aside>
    `,
  },

  {
    key: "report",
    label: "Report",
    blurb: "Cover → executive summary → 3 numbered sections with sub-headings → conclusion → references.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Report title",
          align: "left",
          eyebrow: "Report",
        }),
        block("paragraph", {
          html: "<p>A subtitle or descriptor — what this report covers and the period it's about.</p>",
          align: "left",
          variant: "lead",
        }),
        block("callout", {
          variant: "insight",
          title: "Executive summary",
          html: "<p>Three short bullet points or a one-paragraph summary that conveys the headline findings.</p>",
        }),
        block("heading", { level: 2, text: "1. Background", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Set the scene: what prompted this report, what was already known, what the question is.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "2. Findings", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>The substance of the report. Use sub-headings, lists, or tables as needed.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 3, text: "Key data", align: "left", eyebrow: "" }),
        block("list", {
          ordered: false,
          items: [
            "<p>First finding — short, specific, and quantified where possible.</p>",
            "<p>Second finding.</p>",
            "<p>Third finding.</p>",
          ],
        }),
        block("heading", { level: 2, text: "3. Recommendations", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>What should change as a result. Concrete, actionable.</p>",
          align: "left",
          variant: "default",
        }),
        block("divider", { variant: "rule" }),
        block("heading", { level: 3, text: "References", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Sources and supporting documents.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Report</p>
      <h1>Report title</h1>
      <aside style="border-left:2px solid #5e8aa3;padding-left:.6rem;"><strong>Executive summary</strong></aside>
      <h2>1. Background</h2>
      <h2>2. Findings</h2>
      <h2>3. Recommendations</h2>
    `,
  },

  {
    key: "interview",
    label: "Interview",
    blurb: "Eyebrow → headline → intro paragraph → alternating Q (heading) + A (paragraph) format → closing note.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "A conversation with —",
          align: "left",
          eyebrow: "Interview",
        }),
        block("paragraph", {
          html: "<p>A short framing paragraph: who the interviewee is, when and where the conversation happened, and the through-line.</p>",
          align: "left",
          variant: "lead",
        }),
        block("divider", { variant: "rule" }),
        block("heading", { level: 3, text: "How did this work begin for you?", align: "left", eyebrow: "Q" }),
        block("paragraph", {
          html: "<p>The interviewee's response, lightly edited for clarity.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 3, text: "What surprised you most?", align: "left", eyebrow: "Q" }),
        block("paragraph", {
          html: "<p>Another response.</p>",
          align: "left",
          variant: "default",
        }),
        block("quote", {
          html: "<p>An optional pulled-out line from the conversation.</p>",
          citation: "",
          variant: "pull",
        }),
        block("heading", { level: 3, text: "Where is your practice headed next?", align: "left", eyebrow: "Q" }),
        block("paragraph", {
          html: "<p>A closing answer that leaves space for the future.</p>",
          align: "left",
          variant: "default",
        }),
        block("paragraph", {
          html: "<p><em>This conversation has been edited for length and clarity.</em></p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Interview</p>
      <h1>A conversation with —</h1>
      <p><em>A short framing paragraph.</em></p>
      <h3>Q · How did this work begin?</h3>
      <h3>Q · What surprised you most?</h3>
      <blockquote>An optional pulled-out line.</blockquote>
    `,
  },
];

/**
 * Special-case "blank" preset: an empty document with a friendly label. Not
 * stored in TEMPLATES because it has no `doc` content to clone — `findTemplate`
 * synthesises one each call so the empty-doc reference is fresh.
 */
const BLANK_LABEL = "Blank";
const BLANK_BLURB = "An empty page. Add blocks from the palette as you go.";

export function findTemplate(key: ArticleTemplate): TemplatePreset {
  if (key === "blank") {
    return {
      key: "blank",
      label: BLANK_LABEL,
      blurb: BLANK_BLURB,
      doc: emptyDoc(),
      previewHtml: "",
    };
  }
  const t = TEMPLATES.find((t) => t.key === key);
  return t ?? TEMPLATES[0]!;
}
